export type ProjectSource = 'crm' | 'dev';

export type ProjectStatus = 'planned' | 'active' | 'on_hold' | 'completed' | 'cancelled';
export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'review' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';
export type DevComponent = 'backend' | 'frontend' | 'mobile' | 'gateway' | 'infra' | 'other';

export interface CrmProject {
  id: number;
  customer_id: string;
  customer_name?: string;
  organization_id?: string;
  name: string;
  description?: string;
  project_type: string;
  status: ProjectStatus;
  planned_start?: string;
  planned_end?: string;
  actual_start?: string;
  actual_end?: string;
  budget?: number;
  actual_cost?: number;
  billing_type: string;
  hourly_rate?: number;
  project_manager_id?: string;
  project_manager_name?: string;
  primary_contact_id?: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
  open_tasks?: number;
  created_at?: string;
  updated_at?: string;
}

export interface DevProject {
  id: number;
  organization_id?: string;
  name: string;
  description?: string;
  status: ProjectStatus;
  priority: TaskPriority;
  release_target?: string;
  owner_id?: string;
  owner_name?: string;
  planned_start?: string;
  planned_end?: string;
  actual_start?: string;
  actual_end?: string;
  open_tasks?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ProjectMilestone {
  id: number;
  project_id: number;
  name: string;
  description?: string;
  due_date?: string;
  target_date?: string;
  release_version?: string;
  status: string;
  progress_percent: number;
  sort_order: number;
}

export interface ProjectTask {
  id: number;
  project_id: number;
  milestone_id?: number;
  parent_task_id?: number;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  assigned_to?: string;
  assigned_to_name?: string;
  planned_start?: string;
  planned_end?: string;
  actual_start?: string;
  actual_end?: string;
  estimated_hours?: number;
  progress_percent: number;
  kanban_sort: number;
  component?: DevComponent;
  tech_debt_tag?: string;
  feature_code?: string;
  github_issue_url?: string;
  subtasks?: TaskSubtask[];
}

export interface TaskSubtask {
  id?: number;
  task_id?: number;
  title: string;
  is_done: boolean;
  sort_order?: number;
}

export interface TaskDependency {
  id: number;
  task_id: number;
  depends_on_task_id: number;
  dependency_type: string;
}

export interface TimeEntry {
  id: number;
  project_id: number;
  task_id?: number;
  user_id: string;
  user_name?: string;
  entry_date: string;
  hours: number;
  billable?: boolean;
  notes?: string;
}

export interface ProjectMember {
  id: number;
  project_id: number;
  user_id: string;
  user_name?: string;
  email?: string;
  role: string;
}

export interface ProjectDashboardStats {
  total: number;
  active: number;
  overdue_tasks: number;
  my_tasks: number;
  hours_this_week: number;
}

export interface GanttData {
  tasks: ProjectTask[];
  milestones: ProjectMilestone[];
  dependencies: TaskDependency[];
}

export interface ProjectLeaderCandidate {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  is_global_admin: boolean;
}

export const TASK_STATUSES: TaskStatus[] = ['backlog', 'todo', 'in_progress', 'review', 'done'];
export const PROJECT_STATUSES: ProjectStatus[] = ['planned', 'active', 'on_hold', 'completed', 'cancelled'];
